import type { DirectoryPath, DirectoryPrivate } from '@repo/types/directory';
import type { ComponentProps } from 'react';

import { cn } from '@repo/lib/cn';
import { ArrowLeft, Folder, HardDriveIcon, MoreHorizontal } from 'lucide-react';
import { useMemo } from 'react';

import { Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Props for the FSPath component.
 * @property {DirectoryPrivate} [directory] - The current directory object.
 * @property {DirectoryPath} [rootPath] - The synthetic root directory path.
 * @property {boolean} isLoading - Whether the directory data is loading.
 * @property {boolean} isError - Whether there was an error loading the directory.
 * @property {(id: string | undefined) => void} onRouteChange - Callback for changing the route/directory.
 */
export interface FSPathProps {
  directory?: DirectoryPrivate;
  rootPath?: DirectoryPath;
  isLoading: boolean;
  isError: boolean;
  onRouteChange: (id: string | undefined) => void;
}

export default function FSPath(fsPathProps: FSPathProps) {
  const { rootPath, directory, isLoading, isError, onRouteChange } = fsPathProps;

  const currentDir = useMemo<DirectoryPath | null>(() => {
    if (!directory) return null;
    return {
      name: directory.name,
      id: directory.id,
      parentDirId: directory.parentDir.id,
    };
  }, [directory]);

  const ancestors = useMemo<DirectoryPath[]>(() => {
    if (!directory) return [];
    return [...directory.path];
  }, [directory]);

  const [rootAncestor, ...restAncestors] = ancestors;
  const dropdownPaths: DirectoryPath[] = rootPath ? ancestors : restAncestors;
  const onlyRoot: boolean = !!rootPath && !directory;
  const root: DirectoryPath | undefined = rootPath || rootAncestor;

  const canGoBack = useMemo<boolean | undefined>(() => {
    if (!currentDir) return false;

    if (ancestors.length === 0 && rootPath) return true;

    if (currentDir.parentDirId) {
      const parentIsAccessible = ancestors.some((a) => a.id === currentDir.parentDirId);

      const parentIsRoot = root && root.id === currentDir.parentDirId;

      return parentIsAccessible || parentIsRoot;
    }

    return false;
  }, [currentDir, ancestors, root, rootPath]);

  const handleBackClick = () => {
    if (!canGoBack || !currentDir) return;

    if (ancestors.length === 0 && rootPath) {
      onRouteChange(undefined);
      return;
    }

    if (currentDir.parentDirId) {
      onRouteChange(currentDir.parentDirId);
    }
  };

  return (
    <Breadcrumb className={'flex gap-1'}>
      <Button variant={'ghost'} size={'sm'} onClick={handleBackClick} disabled={!canGoBack} className={cn('h-7 min-h-7 w-7 min-w-7 p-0', !canGoBack && 'hidden')}>
        <ArrowLeft />
      </Button>
      <BreadcrumbList className="h-7">
        {/* Root item */}

        {root && (
          <>
            <DirectoryBreadcrumbItem path={root} onRouteChange={rootPath ? () => onRouteChange(undefined) : onRouteChange}>
              <HardDriveIcon className={cn('size-3.5 lg:hidden', onlyRoot && 'hidden')} />
              <span className={cn(!onlyRoot && 'hidden lg:block')}>{root.name}</span>
            </DirectoryBreadcrumbItem>

            <ParentDirectoriesDropdown paths={dropdownPaths} onRouteChange={onRouteChange} />
          </>
        )}

        {currentDir && (
          <>
            {(ancestors.length > 0 || !!rootPath) && <BreadcrumbSeparator />}
            <BreadcrumbItem>
              <BreadcrumbPage className="max-w-[30ch] truncate text-xs md:text-sm lg:max-w-80">{currentDir.name}</BreadcrumbPage>
            </BreadcrumbItem>
          </>
        )}

        {isLoading && (
          <>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <Skeleton className="h-7 w-14" />
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <Skeleton className="h-7 w-18" />
            </BreadcrumbItem>
          </>
        )}

        {isError && (
          <BreadcrumbItem>
            <BreadcrumbPage className="text-destructive truncate text-sm">Wystąpił błąd.</BreadcrumbPage>
          </BreadcrumbItem>
        )}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

/**
 * Props for a clickable breadcrumb segment representing a directory.
 * @property {(directoryId: string) => void} [onRouteChange] - Callback for changing the route/directory.
 * @property {DirectoryPath} [path] - The directory path object.
 */
export interface DirectoryBreadcrumbItemProps extends ComponentProps<typeof BreadcrumbItem> {
  onRouteChange?: (directoryId: string) => void;
  path?: DirectoryPath;
}

function DirectoryBreadcrumbItem(directoryBreadcrumbItemProps: DirectoryBreadcrumbItemProps) {
  const { onRouteChange, path, className, children, ...rest } = directoryBreadcrumbItemProps;

  if (!path) return null;

  return (
    <BreadcrumbItem onClick={() => onRouteChange?.(path.id)} className={cn('last:text-foreground h-full max-w-[30ch] cursor-pointer truncate text-xs md:text-sm lg:max-w-80', className)} {...rest}>
      {children || path.name}
    </BreadcrumbItem>
  );
}

/**
 * Props for the parent directories dropdown.
 * @property {DirectoryPath[]} paths - Array of parent directory paths.
 * @property {(id: string) => void} onRouteChange - Callback for changing the route/directory.
 */
export interface ParentDirectoriesDropdownProps {
  paths: DirectoryPath[];
  onRouteChange: (id: string) => void;
}

function ParentDirectoriesDropdown(parentDirectoriesDropdownProps: ParentDirectoriesDropdownProps) {
  const { paths, onRouteChange } = parentDirectoriesDropdownProps;
  if (!paths.length) return null;

  return (
    <>
      <BreadcrumbSeparator />
      <BreadcrumbItem className="p-0">
        <DropdownMenu>
          <DropdownMenuTrigger aria-label="Show parent directories">
            <BreadcrumbItem>
              <MoreHorizontal className="size-4" />
            </BreadcrumbItem>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuGroup>
              {paths.map((p) => (
                <PathsDropdownItem key={p.id} path={p} onPathChange={onRouteChange} />
              ))}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </BreadcrumbItem>
    </>
  );
}

/**
 * Props for a single dropdown item representing a directory.
 * @property {DirectoryPath} path - The directory path object.
 * @property {(directoryId: string) => void} onPathChange - Callback for changing the route/directory.
 */
export interface PathsDropdownItemProps {
  path: DirectoryPath;
  onPathChange: (directoryId: string) => void;
}

function PathsDropdownItem(pathsDropdownItemProps: PathsDropdownItemProps) {
  const { path, onPathChange } = pathsDropdownItemProps;
  return (
    <DropdownMenuItem onClick={() => onPathChange(path.id)}>
      <Folder className="size-3.5" />
      {path.name}
    </DropdownMenuItem>
  );
}
